import * as React from 'react';

const styles = require('./ScrollableBlock.css');

const BOTTOM_INDENT = 100;

interface IScrollContainerProps {
  onPageScroll(): void;
  itemHeight: number;
  nextPagePending?: boolean;
  canRequestNextPage?: boolean;
  startItemsCount?: number;
  children: React.ReactNode;
}

interface IPage {
  // разница между видимой частью и высотой страницы
  diffBetweenVisibleHeight: number;
  // количество элементов на странице
  itemsCount: number;
  // высота страницы
  pageHeight: number;
  // позиция страницы относительно верхней части контейнера
  positionTop: number;
}

export const ScrollableBlock: React.FC<IScrollContainerProps> = ({
  onPageScroll,
  itemHeight,
  startItemsCount,
  children,
}) => {
  const containerElement = React.useRef<HTMLDivElement | null>(null);
  const whenRequestNextPage = 2;

  const itemsCount = React.Children.count(children);
  const pageHeight = (itemsCount - whenRequestNextPage) * itemHeight;

  const [pages, setPages] = React.useState<IPage[]>([
    {
      diffBetweenVisibleHeight: 0,
      itemsCount,
      pageHeight,
      positionTop: 0,
    },
  ]);

  React.useEffect(() => {
    const getNextPagePosition = () => {
      return pages.reduce((pos, page) => pos + page.pageHeight, 0);
    };

    const prevItemsCount = itemsCount;
    const nextItemsCount = React.Children.count(children);

    if (!containerElement.current) {
      return;
    }

    const elementHeight = containerElement.current.getBoundingClientRect().height;

    if (startItemsCount) {
      containerElement.current.scrollTop += startItemsCount * itemHeight;
    }

    if (prevItemsCount !== nextItemsCount) {
      const newItemsCount = nextItemsCount - prevItemsCount;
      const newPageHeight = (newItemsCount - whenRequestNextPage) * itemHeight;
      const diffBetweenVisibleHeight = newPageHeight - elementHeight;
      const positionTop = getNextPagePosition();

      const newPages = pages.concat([
        {
          diffBetweenVisibleHeight,
          itemsCount: newItemsCount,
          pageHeight: newPageHeight,
          positionTop,
        },
      ]);

      setPages(newPages);
    }
  }, [children, startItemsCount, itemHeight, itemsCount, pages]);

  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

    if (scrollHeight === clientHeight) {
      return;
    }

    const scrollPositionForNeedRequestNextPage = scrollHeight - clientHeight - BOTTOM_INDENT;

    if (scrollTop >= scrollPositionForNeedRequestNextPage) {
      onPageScroll();
    }
  };

  return (
    <div ref={containerElement} className={styles['container']} onScroll={onScroll}>
      {children}
    </div>
  );
};
